import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { productShortId } from '@/lib/utils';
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight, Check } from 'lucide-react';
import { useCartStore } from '@/data/cart';
import { Button } from '@/components/ui/button';
import SafeImage from '@/components/SafeImage';

export default function CartDrawer() {
  const { items, drawerOpen, closeDrawer, removeItem, updateQuantity, totalPrice, lastAddedId, lastAddedQty, clearLastAdded } = useCartStore();
  const lastAddedItem = lastAddedId ? items.find(i => i.product.id === lastAddedId) : null;
  const total = totalPrice();
  const shipping = total >= 59 ? 0 : 4.99;
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const highlightRef = useRef<HTMLLIElement>(null);
  const totalRef = useRef<HTMLDivElement>(null);

  // Lock scroll + ESC handler
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen, closeDrawer]);

  // Focus & scroll the freshly added item, then briefly highlight the total
  useEffect(() => {
    if (!drawerOpen || !lastAddedId) return;
    const t = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [drawerOpen, lastAddedId]);

  // Auto-dismiss the "added" banner after a few seconds
  useEffect(() => {
    if (!lastAddedId) return;
    const t = setTimeout(() => clearLastAdded(), 4000);
    return () => clearTimeout(t);
  }, [lastAddedId, clearLastAdded]);

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Warenkorb schließen"
        onClick={closeDrawer}
        className={`fixed inset-0 z-[90] bg-foreground/50 backdrop-blur-sm transition-opacity duration-200 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Warenkorb"
        className={`fixed top-0 right-0 z-[95] h-[100dvh] w-full sm:max-w-md bg-background shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-4 sm:px-5 h-14 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 className="font-heading text-base sm:text-lg font-semibold">
              Warenkorb {totalQty > 0 && <span className="text-muted-foreground font-normal">({totalQty})</span>}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Schließen"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Top banner removed — notification now lives above the subtotal in the footer */}

        {/* Items */}
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12">
            <ShoppingBag className="h-12 w-12 text-muted-foreground mb-3" aria-hidden="true" />
            <p className="font-heading text-lg font-semibold mb-1">Dein Warenkorb ist leer</p>
            <p className="text-sm text-muted-foreground mb-6">Entdecke unsere handgefertigten Kunstwerke.</p>
            <Button asChild onClick={closeDrawer}>
              <Link to="/shop">Zum Shop</Link>
            </Button>
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 space-y-3">
            {items.map(({ product, quantity }) => {
              const isLastAdded = product.id === lastAddedId;
              const limits: number[] = [];
              if (product.stock_quantity != null) limits.push(product.stock_quantity);
              if (product.max_order_quantity != null) limits.push(product.max_order_quantity);
              const maxQty = limits.length > 0 ? Math.min(...limits) : Infinity;

              return (
                <li
                  key={product.id}
                  ref={isLastAdded ? highlightRef : undefined}
                  className={`relative flex gap-3 rounded-xl border p-2.5 transition-colors ${
                    isLastAdded
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/30 animate-fade-in'
                      : 'border-border bg-card'
                  }`}
                >
                  <Link to={`/produkt/${productShortId(product.slug)}`} onClick={closeDrawer} className="shrink-0">
                    <SafeImage
                      src={product.image || ''}
                      alt={product.name}
                      width={80}
                      height={80}
                      loading="lazy"
                      decoding="async"
                      wrapperClassName="h-16 w-16 rounded-lg overflow-hidden bg-secondary"
                      className="h-full w-full object-cover"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/produkt/${productShortId(product.slug)}`}
                        onClick={closeDrawer}
                        className="text-sm font-medium hover:text-primary line-clamp-2 flex-1"
                      >
                        {product.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => removeItem(product.id)}
                        className="shrink-0 -mr-1 -mt-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
                        aria-label={`${product.name} entfernen`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="inline-flex items-center rounded-full border border-border bg-background">
                        <button
                          type="button"
                          onClick={() => updateQuantity(product.id, quantity - 1)}
                          disabled={quantity <= 1}
                          aria-label="Menge verringern"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-l-full hover:bg-secondary disabled:opacity-30"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium tabular-nums">{quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(product.id, quantity + 1)}
                          disabled={quantity >= maxQty}
                          aria-label="Menge erhöhen"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-r-full hover:bg-secondary disabled:opacity-30"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="font-heading font-semibold text-sm tabular-nums">
                        {(product.price * quantity).toFixed(2).replace('.', ',')}€
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Footer / total */}
        {items.length > 0 && (
          <footer className="border-t border-border px-4 sm:px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.875rem)] shrink-0 space-y-2.5 bg-background">
            {lastAddedItem && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-start justify-end gap-2 mb-3 animate-fade-in"
              >
                <div className="flex items-start gap-2 rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-3 py-2 text-xs font-medium max-w-full">
                  <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  <span className="line-clamp-2 text-right">
                    {lastAddedQty}× {lastAddedItem.product.name} hinzugefügt
                  </span>
                </div>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Zwischensumme</span>
              <span className="tabular-nums">{total.toFixed(2).replace('.', ',')}€</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Versand</span>
              <span className="tabular-nums">
                {shipping === 0 ? 'Kostenlos' : `${shipping.toFixed(2).replace('.', ',')}€`}
              </span>
            </div>
            <div
              ref={totalRef}
              className={`flex items-center justify-between font-heading text-lg font-bold pt-2 border-t border-border rounded transition-colors ${
                lastAddedId ? 'animate-fade-in text-primary' : ''
              }`}
            >
              <span>Gesamt</span>
              <span className="tabular-nums">{(total + shipping).toFixed(2).replace('.', ',')}€</span>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <Button asChild size="lg" className="h-12 w-full font-body" onClick={closeDrawer}>
                <Link to="/warenkorb">
                  Zur Kasse <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <button
                type="button"
                onClick={closeDrawer}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Weiter einkaufen
              </button>
            </div>
          </footer>
        )}
      </aside>
    </>
  );
}
